import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { useSpeechToText } from '../../hooks/useSpeechToText';
import { addRecent } from './recentSearchesSlice';
import { RecentSearchesDropdown } from './components/RecentSearchesDropdown';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Image,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import { BrandLogo } from '../../components';
import { ItemThumbnail } from '../../components/ItemThumbnail';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import { selectUser } from '../auth/authSlice';
import { CustodyChip } from '../inventory/components/CustodyChip';
import {
  deleteInventoryItem,
  loadInventoryItemsEnhanced,
  mergeInventoryItem,
  selectActiveFiltersCount,
  selectBrowseError,
  selectBrowseLoading,
  selectCategories,
  selectDeletingItemIds,
  selectFilteredInventoryItems,
  selectFilters,
  selectHasMore,
  selectIsLoadingMore,
  selectRawInventoryCount,
  selectTotalCount,
  setFilters,
  normalizeScope,
  type BrowseScope,
} from './browseSlice';
import { api } from '../../api/remembrApi';
import {
  loadDashboardData,
  selectInventorySummaryStats,
  selectNetWorthStats,
} from '../dashboard/dashboardSlice';
import type { EnhancedInventoryItem } from '../../api/types';
// Sprint 2 (FE-9.4) — Group-by pill row + sectioned layouts.
import { GroupByPills, readPersistedLayout } from './components/GroupByPills';
import { groupItemsBy, type BrowseLayout, type BrowseSection } from './groupItemsBy';
import {
  selectGroups,
  loadGroupInventory,
  selectGroupInventoryItems,
} from '../sharing/groupsSlice';
import {
  loadUserShares,
  selectInboundLifecycleShares,
} from '../sharing/sharingSlice';
// Sprint 2.5 (FE-9.4.1) — Scenes layout via /users/files-with-inventory.
import { loadScenesPage, selectScenesCache } from './browseSlice';
// FE-204 — derive borrow / lent-out pill state from item ownership +
// current custodian columns. Pure helper, unit-tested separately.
import { deriveItemPossessionState } from './itemPossessionState';
// FE-210 — DeleteSceneSheet for the ⋮ button on scene cards.
import { DeleteSceneSheet } from './components/DeleteSceneSheet';
import type { FileWithInventoryResponse } from '../../api/types';
// Sprint 3 (FE-11.5.C, Chunk 9.5 Phase B) — featured-tags strip.
import { FeaturedTagsStrip } from './components/FeaturedTagsStrip';
import { TagTypeahead } from './components/TagTypeahead';
import { LocationTypeahead } from './components/LocationTypeahead';
import { parseTags } from './groupItemsBy';
// Chunk 9.5 / FE-11.5.A — Location → Map view (Leaflet + OSM).
import { LocationMapView } from './components/LocationMapView';
import { NotificationBell } from '../notifications/components/NotificationBell';
// FE #124.5 — Top-bar 🕒 clock-rewind icon (always visible).
import { ScanHistoryButton } from '../scans/components/ScanHistoryButton';
import { perf } from '../../utils/logger';

/** Sprint 2.5 (FE-9.4.1) — scene-card subtitle. Phase A uses
 *  formatted date + item count; Phase B (FE-11.5.B) replaces with
 *  AI scene_label as the title and demotes the date here. */
function sceneSubtitle(file: FileWithInventoryResponse): string {
  const itemCount = file.inventory_items?.length ?? 0;
  const itemLabel = itemCount === 1 ? '1 item' : `${itemCount} items`;
  let dateLabel = '';
  try {
    const d = new Date(file.created_at);
    if (!Number.isNaN(d.getTime())) {
      dateLabel = d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    }
  } catch {
    /* fall back to no date */
  }
  return dateLabel ? `${dateLabel} · ${itemLabel}` : itemLabel;
}

/**
 * Browse / Items — redesigned as a single unified grid.
 *
 * Dropped from the legacy screen:
 *   - View-mode toggle (files / inventory / relationship)
 *   - FileInventoryCard embed
 *   - Filter drawer with value range + verified toggle
 *
 * Kept:
 *   - Search (filters.search via browseSlice)
 *   - Category chips (filters.category)
 *   - Delete per-item with confirmation
 *   - Pull-to-refresh
 *   - Tap → ItemDetail
 */
export const BrowseScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const { width } = useWindowDimensions();
  const numColumns = width >= 768 ? 3 : 2;

  const user = useAppSelector(selectUser);
  const items = useAppSelector(selectFilteredInventoryItems);
  const isLoading = useAppSelector(selectBrowseLoading);
  const isLoadingMore = useAppSelector(selectIsLoadingMore);
  const hasMore = useAppSelector(selectHasMore);
  const totalCount = useAppSelector(selectTotalCount);
  const error = useAppSelector(selectBrowseError);
  const filters = useAppSelector(selectFilters);
  const categories = useAppSelector(selectCategories);
  const deletingIds = useAppSelector(selectDeletingItemIds);
  const inventorySummary = useAppSelector(selectInventorySummaryStats);
  const netWorth = useAppSelector(selectNetWorthStats);
  const crews = useAppSelector(selectGroups);
  // PR11 — items the viewer claimed via a share-link AND has now
  // marked returned (or the lender has confirmed receipt) should
  // render greyed/de-emphasized so they no longer compete visually
  // with active inventory. The duplicated row stays in inventory
  // until backend archival (Phase 2) — this is the FE-side visual
  // bandaid until then. Map share_id → status, looked up by each
  // card via item.claimed_from_share_id.
  const inboundLifecycleShares = useAppSelector(selectInboundLifecycleShares);
  const shareStatusByShareId = useMemo(() => {
    const m: Record<number, 'CLAIMED' | 'RETURNED' | 'COMPLETED'> = {};
    for (const s of inboundLifecycleShares) {
      m[s.share_id] = s.status as 'CLAIMED' | 'RETURNED' | 'COMPLETED';
    }
    return m;
  }, [inboundLifecycleShares]);

  // Sprint 2 (FE-9.4) — Group-by layout. Default = 'recent' (today's
  // flat grid). sessionStorage remembers selection across navigation
  // within the same tab session, but a fresh tab resets to Recent.
  const [layout, setLayout] = useState<BrowseLayout>(() => readPersistedLayout());
  // Chunk 9.5 / FE-11.5.A (Stage 2A) — the [Map | List] toggle was
  // removed. Location layout now always renders the full-bleed map.
  // List mode would have been a legacy grouped-by-location-text
  // view; the map covers every use case it served.

  // Sprint 2.5 (FE-9.4.1) — Scenes layout reads from a server-side
  // paginated cache (one FileRecord per scene, with nested inventory
  // items). Decoupled from the inventory pagination so toggling
  // between Recent and Scenes doesn't fight for state.
  const scenesCache = useAppSelector(selectScenesCache);

  // FE-210 — DeleteSceneSheet target. null = closed. Holds the full
  // FileWithInventoryResponse row so the sheet can render the scene
  // title + item count + run the right cascade flag.
  const [deleteTargetScene, setDeleteTargetScene] =
    useState<FileWithInventoryResponse | null>(null);

  // Auto-load the first page of scenes when the user enters Scenes
  // layout for the first time. Subsequent visits hit the cache.
  useEffect(() => {
    if (layout === 'scenes' && !scenesCache.hasLoaded && !scenesCache.loading) {
      dispatch(loadScenesPage({ mode: 'replace' }) as any);
    }
  }, [layout, scenesCache.hasLoaded, scenesCache.loading, dispatch]);

  // Sprint 2.5 (FE-9.4.3) — Crew layout fetches a 5-item preview per
  // crew on first switch. groupsSlice.inventory[crewId].hasLoaded
  // short-circuits subsequent re-pickings so we don't refetch on every
  // toggle. Skips the synthetic Personal pseudo-group (id=-1) since it
  // has no real backend inventory endpoint.
  const groupsInventoryState = useAppSelector(
    (s: any) => s.groups?.inventory ?? {},
  );
  useEffect(() => {
    if (layout !== 'crew') return;
    const realCrews = crews.filter((c) => c.id > 0);
    for (const crew of realCrews) {
      const cached = groupsInventoryState[crew.id];
      if (!cached?.hasLoaded && !cached?.loading) {
        dispatch(
          loadGroupInventory({ groupId: crew.id, limit: 5, offset: 0, mode: 'replace' }) as any,
        );
      }
    }
    // We intentionally only fire on layout flip to 'crew'. The cache
    // entries themselves are checked for hasLoaded short-circuit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, crews.length]);

  // FE-204 — viewer-name lookup for the borrower / lent-out pill.
  //
  // ItemGridCard renders "From {name}" or "Lent to {name}" using
  // whatever cached group_member rows we already have. Same pattern
  // as CustodyChip on the Crew tab. Memoized once per group-details
  // snapshot so every card re-uses the same Map reference (stable
  // input for ItemGridCard's React.memo equality below).
  const groupDetailsMap = useAppSelector(
    (s: any) => s.groups?.detail ?? {},
  );
  const userNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const key of Object.keys(groupDetailsMap)) {
      const detail = groupDetailsMap[Number(key)];
      const members = detail?.members ?? [];
      for (const mem of members) {
        if (mem.user_id == null) continue;
        if (m.has(mem.user_id)) continue;
        const name = (mem.name && mem.name.trim()) || null;
        const emailPrefix =
          mem.email && mem.email.trim() ? mem.email.split('@')[0] : null;
        const label = name || emailPrefix;
        if (label) m.set(mem.user_id, label);
      }
    }
    return m;
  }, [groupDetailsMap]);
  const resolveUserName = useCallback(
    (userId: number): string | null => userNameById.get(userId) ?? null,
    [userNameById],
  );

  // Section data for non-recent layouts. Recent stays on the existing
  // FlatList path below — no behavioral change. Scenes mode reads
  // from the new server-side cache (FE-9.4.1); other modes still
  // client-side group the loaded inventory.
  const sections = useMemo<BrowseSection[]>(() => {
    if (layout === 'recent') return [];
    if (layout === 'scenes') {
      // FE-9.4.1 + FE-11.5.B: project FileWithInventoryResponse[] from
      // the cache into BrowseSection[]. Title prefers the AI-generated
      // `scene_label` (backend#109, 1-3 word label like "Kitchen" or
      // "Saddleridge job site") and falls back to the raw filename when
      // the label hasn't been generated yet. inventory_items become
      // section.data (cast to EnhancedInventoryItem for ItemGridCard
      // render compatibility — same pragmatic cast as the other layouts).
      //
      // FE #255 — Snapshots-layout search.  The hoisted SearchBar above
      // the layout pills writes `filters.search` for every layout, but
      // the scenes cache is keyed on the BE response, not on a search
      // query.  Filter client-side: keep scenes whose label / filename
      // OR any nested inventory item name matches the query
      // (case-insensitive substring).  Empty query is a no-op.
      const q = (filters.search ?? '').trim().toLowerCase();
      const allScenes = scenesCache.scenes;
      const visible = q
        ? allScenes.filter((file) => {
            const label = (file.scene_label ?? '').toLowerCase();
            const fname = (file.filename ?? '').toLowerCase();
            if (label.includes(q) || fname.includes(q)) return true;
            for (const it of file.inventory_items ?? []) {
              if (((it as any).item_name ?? '').toLowerCase().includes(q)) {
                return true;
              }
            }
            return false;
          })
        : allScenes;
      return visible.map((file) => {
        const label = (file.scene_label ?? '').trim();
        return {
          key: `scene-${file.id}`,
          title: label || file.filename || `Snapshot ${file.id}`,
          subtitle: sceneSubtitle(file),
          heroPhotoUrl: file.storage_url,
          data: (file.inventory_items ?? []) as unknown as EnhancedInventoryItem[],
        };
      });
    }
    // FE #257 — Tag layout w/ multi-select filter.  When the user has
    // chipped one or more tags from TagTypeahead, collapse the
    // sectioned-by-first-tag view into a single AND-intersection
    // section that contains ONLY items whose `tags` field includes
    // EVERY selected tag (case-insensitive).  This is Linear's saved-
    // view filter formula semantics; the section title spells out the
    // operator so users see the AND chain explicitly.
    if (layout === 'tag' && (filters.tags?.length ?? 0) > 0) {
      const wanted = filters.tags!.map((t) => t.toLowerCase());
      const matches = (items as unknown as EnhancedInventoryItem[]).filter(
        (it) => {
          const have = new Set(parseTags(it.tags).map((t) => t.toLowerCase()));
          for (const w of wanted) if (!have.has(w)) return false;
          return true;
        },
      );
      const title = filters.tags!.join(' AND ');
      return [
        {
          key: `tag-filter-${wanted.join('+')}`,
          title,
          subtitle:
            matches.length === 1 ? '1 item' : `${matches.length} items`,
          data: matches,
        },
      ];
    }
    // selectFilteredInventoryItems returns the InventoryItemResponse
    // shape; for the grouping helper's purposes the visual fields
    // (item_name / source_file_id / location / tags / primary_photo_url
    // / source_image_url) are interchangeable. Cast pragmatically — the
    // helper only reads, never writes, those fields.
    const sectioned = groupItemsBy(
      layout,
      items as unknown as EnhancedInventoryItem[],
      { crews },
    );
    // Search-UX §3.5 — Crew layout: filter the list of crews by name
    // when filters.search is non-empty.  Same hoisted SearchBar; the
    // placeholder copy nudges the user that this surface filters
    // CREWS, not items inside them (the per-crew item search lives
    // inside GroupDetail per §3.6).
    if (layout === 'crew') {
      const q = (filters.search ?? '').trim().toLowerCase();
      if (q) return sectioned.filter((s) => s.title.toLowerCase().includes(q));
    }
    return sectioned;
  }, [layout, items, crews, scenesCache.scenes, filters.search, filters.tags]);

  // Bootstrap dashboard stats so the metrics header has data even if the
  // user lands here before visiting the Dashboard tab.
  useEffect(() => {
    if (!inventorySummary) dispatch(loadDashboardData() as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Sprint 8 / Issue 2 — share-aware CustodyChip needs `/users/shares`
  // populated to render "Lent to X" / "Borrowed from X" pills. The
  // Dashboard mount also dispatches this, but the user can deep-link
  // straight to Items, so re-fire here. Thunk de-dupes server-side
  // (no spam).
  useEffect(() => {
    dispatch(loadUserShares({} as any));
  }, [dispatch]);

  // FE-255 PR5 — refetch when user returns to the inventory tab. The
  // page-1 effect re-runs whenever filters change, but a focus event
  // alone (back from ItemDetail / SceneViewer / a sibling tab) doesn't
  // change filters → list can sit stale for the whole session. Refetch
  // page 1 + the shares cache so custody chips reflect any cross-screen
  // handoffs / returns.
  const refetchBrowse = useCallback(() => {
    dispatch(loadInventoryItemsEnhanced({ refresh: true }) as any);
    dispatch(loadUserShares({} as any));
  }, [dispatch]);
  useRefetchOnFocus(refetchBrowse);

  // Single source of truth for "load page 1": fires once on mount AND
  // every time a server-side filter (search, category) changes. Each
  // dispatch returns an abortable promise; the previous in-flight
  // request is aborted so search-as-you-type doesn't race itself
  // (backend takes 0.5–2.5s — without abort, an earlier 'k' response
  // can land *after* a later 'kit' response and overwrite the right
  // results).
  const inflightRef = useRef<{ abort: () => void } | null>(null);
  useEffect(() => {
    // FE-251 — log every scope/search/category change so we can
    // see exactly when refetches fire vs when the UI updates.
    perf.mark('BrowseScreen:loadEffect', {
      search: filters.search ?? null,
      category: filters.category ?? null,
      scope: filters.scope ?? null,
      hadInflight: !!inflightRef.current,
    });
    if (inflightRef.current) {
      perf.mark('BrowseScreen:abortPreviousLoad');
      inflightRef.current.abort();
    }
    const promise = dispatch(loadInventoryItemsEnhanced({ refresh: true }) as any);
    inflightRef.current = promise;
    return () => {
      // On unmount, also abort — prevents a stale fulfilled action
      // from landing in a slice that's about to be reset.
      promise.abort?.();
    };
    // FE-248 v2 follow-up — filters.scope was missing from this dep
    // array. When the user clicked "Just held" from "Owned + held",
    // the scope changed but no refetch fired, so the FE just
    // client-filtered the previously-loaded (and now wrong-shape)
    // pages. Since the BE response for held_by_me=true uses
    // sort_by=updated_at and held items sit at the TOP, offset=0 is
    // exactly where they live — but without a refresh we'd jump to
    // offset=20+ on the first onEndReached and miss them entirely.
    // Including scope in deps forces a fresh fetch from offset 0
    // every time the user changes the show-toggle.
  }, [dispatch, filters.search, filters.category, filters.scope]);

  const initials = (user?.full_name || 'U')
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const categoryChips = useMemo(() => (categories || []).slice(0, 12), [categories]);

  const handleRefresh = useCallback(() => {
    if (inflightRef.current) inflightRef.current.abort();
    const promise = dispatch(loadInventoryItemsEnhanced({ refresh: true }) as any);
    inflightRef.current = promise;
  }, [dispatch]);

  // FE-248 v2 follow-up — track whether successive fetches are
  // producing NEW visible items. Server pagination keeps reporting
  // hasMore=true even when the client-side filter (e.g. scope='held')
  // drops every row that comes back, which previously created an
  // infinite "Loading more..." loop.
  //
  // V1 of this fix watched `items.length` (filtered count) — that
  // was useless because the filtered count by definition DOESN'T
  // change on dud pages, so the useEffect that incremented the
  // counter never fired. Counter stayed at 0 forever → pagination
  // never stopped.
  //
  // V2 (this) watches the RAW pre-filter count. Every server page
  // that lands grows that count by 100; if the filtered count
  // doesn't grow alongside it, we know the page was a dud and bump
  // the stuck counter. Two consecutive duds → freeze pagination.
  const rawInventoryCount = useAppSelector(selectRawInventoryCount);
  const stuckPagesRef = useRef({
    lastRawCount: 0,
    lastFilteredCount: 0,
    stuckCount: 0,
  });
  useEffect(() => {
    // FE-251 — stuck-page detection applies whenever we apply a
    // client-side filter on top of the BE response. 'all' is a
    // passthrough so no possibility of zero-yield pages there; the
    // other three pills ('mine', 'lent', 'borrowed') all filter
    // client-side and can produce empty results from a non-empty
    // BE page. Reset the counter on 'all'; track for the rest.
    const scope = normalizeScope(filters.scope);
    if (scope === 'all') {
      stuckPagesRef.current = {
        lastRawCount: rawInventoryCount,
        lastFilteredCount: items.length,
        stuckCount: 0,
      };
      return;
    }
    // First snapshot OR raw count didn't change (no new fetch landed
    // yet, e.g. mount or scope change). Just record and move on.
    if (rawInventoryCount === stuckPagesRef.current.lastRawCount) {
      stuckPagesRef.current.lastFilteredCount = items.length;
      return;
    }
    // A new page landed (rawInventoryCount grew). Did our filtered
    // list grow with it?
    const filteredGrew = items.length > stuckPagesRef.current.lastFilteredCount;
    if (filteredGrew) {
      stuckPagesRef.current = {
        lastRawCount: rawInventoryCount,
        lastFilteredCount: items.length,
        stuckCount: 0,
      };
    } else {
      stuckPagesRef.current = {
        lastRawCount: rawInventoryCount,
        lastFilteredCount: items.length,
        stuckCount: stuckPagesRef.current.stuckCount + 1,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawInventoryCount, items.length, filters.scope]);

  const handleEndReached = useCallback(() => {
    // Pagination IS allowed during search/filter — the same query
    // params (search, category, etc.) flow through the thunk on the
    // next page, so the server returns the next slice of matches.
    if (isLoadingMore || isLoading || !hasMore) return;
    // FE-248 v2 follow-up — when scope='held' the BE response isn't
    // pre-filtered (we client-filter after), so a server page can
    // come back with 0 visible items. After 2 consecutive zero-yield
    // pages, stop auto-loading — the user can pull-to-refresh if
    // they think there's more.
    // FE-251 — apply stuck-page guard for ANY scope that filters
    // client-side (every pill except 'all'). Two consecutive zero-
    // yield pages → freeze auto-pagination; user can pull-to-refresh.
    const scope = normalizeScope(filters.scope);
    if (scope !== 'all' && stuckPagesRef.current.stuckCount >= 2) return;
    dispatch(loadInventoryItemsEnhanced({ refresh: false }) as any);
  }, [dispatch, isLoadingMore, isLoading, hasMore, filters.scope]);

  const handleClearSearch = useCallback(() => {
    dispatch(setFilters({ ...filters, search: '' }));
  }, [dispatch, filters]);

  const handleSelectCategory = useCallback(
    (category: string | undefined) => {
      dispatch(setFilters({ ...filters, category }));
    },
    [dispatch, filters],
  );

  const navigateToUpload = useCallback(() => {
    navigation.navigate('Upload');
  }, [navigation]);

  const handleOpen = (item: EnhancedInventoryItem) => {
    // Pass `itemId` as the route param so the URL is valid +
    // refreshable + shareable (`/item/4856`). `item` is still
    // forwarded for the in-session passedItem fast-path (saves a
    // re-fetch on tap), but React Navigation strips object params
    // from the URL — only the scalar `itemId` survives serialization.
    const id = (item as any)?.id;
    navigation.navigate('ItemDetail', {
      itemId: id != null ? String(id) : undefined,
      item,
    });
  };

  const handleDelete = (item: EnhancedInventoryItem) => {
    const run = async () => {
      try {
        await dispatch(deleteInventoryItem(String((item as any).id))).unwrap();
        Toast.show({ type: 'success', text1: 'Item deleted' });
      } catch (err: any) {
        Toast.show({
          type: 'error',
          text1: 'Could not delete',
          text2: err?.message || 'Please try again.',
        });
      }
    };
    if (typeof window !== 'undefined' && window.confirm) {
      if (window.confirm(`Delete "${item.item_name}"?`)) run();
    } else {
      Alert.alert('Delete item', `Delete "${item.item_name}"?`, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => run() },
      ]);
    }
  };

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          <BrandLogo variant="horizontal" height={24} tone="color" />
          <View style={styles.topBarRight}>
            {/* FE #124.5 — Scan history shortcut. */}
            <ScanHistoryButton
              onPress={() => navigation.navigate('ScanHistory')}
            />
            <NotificationBell
              onPress={() => navigation.navigate('Notifications')}
            />
            <Pressable
              onPress={() => navigation.navigate('Profile')}
              style={styles.avatar}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Profile"
            >
              <Text style={styles.avatarText}>{initials}</Text>
            </Pressable>
          </View>
        </View>
      </View>

      {/* Sprint 2 (FE-9.4) — Group-by pill row. Pinned above the
          FlatList / sectioned view so it doesn't scroll out of reach.
          On the Location layout it floats translucently over the
          full-bleed map (see absolute-positioned wrapper below). */}
      <GroupByPills value={layout} onChange={setLayout} />

      {/* FE-251 — 4-pill scope row: All items / My Items / Items lent
          / Items Borrowed. Each maps to a concrete (owner, custodian)
          quadrant. See browseSlice.ts → BrowseScope JSDoc for the
          mapping. Hidden on the location layout where the map already
          filters by GPS. */}
      {layout !== 'location' && (
        <ScopePillRow
          value={normalizeScope(filters.scope)}
          onChange={(next) => dispatch(setFilters({ ...filters, scope: next }))}
        />
      )}

      {/* FE #255 — Hoisted SearchBar.  Previously the search input only
          rendered inside `BrowseListHeader` (the FlatList header used by
          the Recent layout), so switching to Snapshots / Location / Tag
          / Crew silently dropped the search affordance — `filters.search`
          stayed live in Redux but no UI showed or accepted it.  Hoisting
          here means the same Redux `filters.search` drives EVERY layout
          (every layout filters its rows by the same query), matching
          Apple Photos' Library tab + Notion's per-database search. */}
      <View style={styles.hoistedSearchBar}>
        <SearchBar />
      </View>

      {/* FE #257 — TagTypeahead, only mounted on the Tag layout.  The
          Notion multi-select picker drives `filters.tags[]`; the
          `sections` useMemo below collapses to a single AND-intersection
          section when that array is non-empty. */}
      {layout === 'tag' && <TagTypeahead />}

      {/* FE #256 — LocationTypeahead, only mounted on the Location
          layout.  Picking a suggestion sets `filters.location` AND
          switches layout back to Recent so the user lands on the
          filtered grid (Linear's "view-scoped filter with expand-
          to-global escape hatch" pattern).  Layout is component-local
          state here, so we pass the setter via `onAfterPick`. */}
      {layout === 'location' && (
        <LocationTypeahead onAfterPick={() => setLayout('recent')} />
      )}

      {/* Chunk 9.5 / FE-11.5.A Stage 2A — Full-bleed Memory Map.
          The map fills the entire viewport between the floating top
          chrome and the bottom nav. No [Map | List] toggle anymore —
          map is the only Location view. */}
      {layout === 'location' ? (
        <View style={styles.fullBleedMap} testID="full-bleed-map-container">
          <LocationMapView items={items} onItemPress={handleOpen} />
        </View>
      ) : layout !== 'recent' ? (
        <BrowseSectionedView
          sections={sections}
          isLoading={layout === 'scenes' ? scenesCache.loading : isLoading}
          onRefresh={
            layout === 'scenes'
              ? () => dispatch(loadScenesPage({ mode: 'replace' }) as any)
              : handleRefresh
          }
          onItemPress={handleOpen}
          onItemDelete={handleDelete}
          onCrewPress={(groupId) =>
            navigation.navigate('GroupDetail', { groupId: String(groupId) })
          }
          onScenePress={(fileId) =>
            navigation.navigate('SceneViewer', { fileId: String(fileId) })
          }
          onSceneDeletePress={(fileId) => {
            const found = scenesCache.scenes.find((s) => s.id === fileId);
            if (found) setDeleteTargetScene(found);
          }}
          deletingIds={deletingIds}
          numColumns={numColumns}
          mode={layout}
          scenesPagination={
            layout === 'scenes'
              ? {
                  total: scenesCache.total,
                  loaded: scenesCache.scenes.length,
                  hasMore: scenesCache.hasMore,
                  loading: scenesCache.loading,
                }
              : undefined
          }
          onLoadMoreScenes={
            layout === 'scenes'
              ? () => dispatch(loadScenesPage({ mode: 'append' }) as any)
              : undefined
          }
          searchQuery={filters.search ?? undefined}
        />
      ) : (
      <FlatList
        data={items}
        keyExtractor={keyExtractor}
        numColumns={numColumns}
        key={`cols-${numColumns}`}
        contentContainerStyle={styles.listContent}
        columnWrapperStyle={numColumns > 1 ? styles.columnWrapper : undefined}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.4}
        // Perf props: defaults render every row up-front and never recycle
        // off-screen rows on web, which made the FlatList stutter on each
        // keystroke even after SearchBar isolation.
        initialNumToRender={12}
        maxToRenderPerBatch={8}
        windowSize={5}
        removeClippedSubviews
        refreshControl={
          <RefreshControl
            // Don't show pull-to-refresh spinner during the per-keystroke
            // search refetch — that fires the empty-state "loading" branch
            // when items.length===0 and feels like a full-screen flash.
            // SearchBar surfaces an inline spinner instead.
            refreshing={isLoading && items.length === 0}
            onRefresh={handleRefresh}
            tintColor={fpColors.primary}
          />
        }
        ListHeaderComponent={
          <BrowseListHeader
            totalCount={totalCount}
            inventorySummary={inventorySummary}
            netWorth={netWorth}
            categoriesLength={categories?.length ?? 0}
            categoryChips={categoryChips}
            activeCategory={filters.category}
            isDesktop={numColumns >= 3}
            onAdd={navigateToUpload}
            onSelectCategory={handleSelectCategory}
          />
        }
        renderItem={({ item }) => {
          // PR11 — resolve the share-lifecycle status for this item
          // (null when not a share-claim row or the share is no
          // longer in our cache). Passing the status down lets the
          // card render its greyed/Returned visual without an extra
          // selector per card.
          const sid = (item as any).claimed_from_share_id as
            | number
            | null
            | undefined;
          const claimedShareStatus = sid != null
            ? shareStatusByShareId[sid] ?? null
            : null;
          return (
            <ItemGridCard
              item={item as EnhancedInventoryItem}
              isDeleting={deletingIds.includes(String((item as any).id))}
              claimedShareStatus={claimedShareStatus}
              viewerUserId={user?.id ?? null}
              resolveUserName={resolveUserName}
              onPress={() => handleOpen(item as EnhancedInventoryItem)}
              onDelete={() => handleDelete(item as EnhancedInventoryItem)}
            />
          );
        }}
        ListFooterComponent={(() => {
          if (items.length === 0) return null;
          // FE-248 v2 follow-up — when held-only pagination is frozen
          // (stuckCount >= 2 after consecutive zero-yield fetches), the
          // "Loading more..." footer was still rendering because hasMore
          // stayed true at the server level. Mute the footer entirely
          // in that case — the visible count IS the full count from
          // the user's perspective.
          // FE-251 — generalize stuck-state footer for all
          // client-filtered pills (mine/lent/borrowed).
          const scope = normalizeScope(filters.scope);
          const filteredStuck =
            scope !== 'all' && stuckPagesRef.current.stuckCount >= 2;
          if (isLoadingMore && !filteredStuck) {
            return (
              <View style={styles.footer}>
                <ActivityIndicator color={fpColors.primary} />
                <Text style={styles.footerText}>Loading more…</Text>
              </View>
            );
          }
          if (filteredStuck) {
            const noun =
              scope === 'mine'
                ? 'item'
                : scope === 'lent'
                ? 'lent item'
                : 'borrowed item';
            return (
              <View style={styles.footer}>
                <Text style={styles.footerText}>
                  {items.length} {noun}{items.length === 1 ? '' : 's'}
                </Text>
              </View>
            );
          }
          if (!hasMore && totalCount > 0) {
            return (
              <View style={styles.footer}>
                <Text style={styles.footerText}>
                  {items.length} of {totalCount} items
                </Text>
              </View>
            );
          }
          return null;
        })()}
        ListEmptyComponent={
          <View style={styles.empty}>
            {/* Four states, in priority order:
                1. loading + zero items → spinner
                2. error + zero items   → error + retry CTA
                3. search active + zero results → search-specific empty
                4. clean empty          → onboarding CTA
                Distinguishing #3 from #4 matters: searching for
                something the user doesn't own otherwise looks identical
                to a brand-new empty inventory ("No items yet" + "Add
                new item"), which is wildly misleading. */}
            {isLoading ? (
              <>
                <ActivityIndicator size="small" color={fpColors.primary} />
                <Text style={styles.emptyTitle}>
                  Loading your inventory{'\u2026'}
                </Text>
                <Text style={styles.emptyMeta}>
                  This can take a few seconds on first load.
                </Text>
              </>
            ) : error ? (
              <>
                <MaterialIcons
                  name="error-outline"
                  size={36}
                  color={fpColors.error}
                />
                <Text style={styles.emptyTitle}>Could not load items</Text>
                <Text style={styles.emptyMeta}>{String(error)}</Text>
                <Pressable onPress={handleRefresh} style={styles.emptyCTA}>
                  <MaterialIcons
                    name="refresh"
                    size={18}
                    color={fpColors.onPrimary}
                  />
                  <Text style={styles.emptyCTAText}>Retry</Text>
                </Pressable>
              </>
            ) : filters.search ? (
              <>
                <MaterialIcons
                  name="search-off"
                  size={36}
                  color={fpColors.outline}
                />
                <Text style={styles.emptyTitle}>
                  No matches for {'“'}{filters.search}{'”'}
                </Text>
                <Text style={styles.emptyMeta}>
                  Try a different word, or clear the search to see your
                  full inventory.
                </Text>
                <Pressable
                  onPress={handleClearSearch}
                  style={styles.emptyCTA}
                >
                  <MaterialIcons
                    name="close"
                    size={18}
                    color={fpColors.onPrimary}
                  />
                  <Text style={styles.emptyCTAText}>Clear search</Text>
                </Pressable>
              </>
            ) : (
              <>
                <MaterialIcons
                  name="inventory"
                  size={36}
                  color={fpColors.outline}
                />
                <Text style={styles.emptyTitle}>No items yet</Text>
                <Text style={styles.emptyMeta}>
                  Add your first item to start building your inventory.
                </Text>
                <Pressable
                  onPress={navigateToUpload}
                  style={styles.emptyCTA}
                >
                  <MaterialIcons
                    name="add"
                    size={18}
                    color={fpColors.onPrimary}
                  />
                  <Text style={styles.emptyCTAText}>Add new item</Text>
                </Pressable>
              </>
            )}
          </View>
        }
      />
      )}
      {/* FE-210 — Delete-scene confirmation sheet. Mount at the
          BrowseScreen root so it overlays the entire layout (incl.
          full-bleed map mode). State holds the full FileWith-
          InventoryResponse so the sheet can render scene title +
          item count without re-fetching. */}
      <DeleteSceneSheet
        visible={deleteTargetScene != null}
        scene={deleteTargetScene}
        onClose={() => setDeleteTargetScene(null)}
      />
    </View>
  );
};

/* ————— Presentational pieces ————— */

const keyExtractor = (it: any) => String(it.id);

/**
 * FE-221 / BE-210 — Scope pill row.
 *
 * Lets the user widen the Items grid to include items they currently
 * hold via in-crew handoff. Mounted directly under the `GroupByPills`
 * row so the two filters stack naturally without competing for layout
 * space.
 *
 * Defaults to 'both' so a first-time visitor sees their owned items
 * plus anything they currently hold. 'owned' restores the legacy
 * owner-only view; 'held' is for the borrower POV (rarely needed but
 * useful when the user wants to see exactly what they're holding).
 *
 * Persists nowhere — fresh tab reset matches `GroupByPills`'s "Recent"
 * default behavior.
 */
const ScopePillRow: React.FC<{
  value: BrowseScope;
  onChange: (next: BrowseScope) => void;
}> = ({ value, onChange }) => {
  // FE-251 — 4 mutually-disjoint pills mapping to the (owner, custodian)
  // quadrants. See browseSlice.ts → BrowseScope JSDoc for the full
  // mapping. Order chosen for scan-ability: widest set first
  // (a glance answer), then drill-down options ordered by frequency.
  // FE #207 — shorter labels so all 4 pills fit on 390px iPhone 13.
  // Previously "Items Borrowed" truncated to "Items Borrow" mid-render.
  const opts: Array<{ key: BrowseScope; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'mine', label: 'Mine' },
    { key: 'lent', label: 'Lent out' },
    { key: 'borrowed', label: 'Borrowed' },
  ];
  return (
    <View style={scopeStyles.row} testID="browse-scope-pills">
      <Text style={scopeStyles.label}>Show:</Text>
      {opts.map((opt) => {
        const active = opt.key === value;
        return (
          <Pressable
            key={opt.key}
            onPress={() => onChange(opt.key)}
            accessibilityRole="button"
            accessibilityLabel={`Scope: ${opt.label}`}
            accessibilityState={{ selected: active }}
            testID={`browse-scope-pill-${opt.key}`}
            style={({ pressed }) => [
              scopeStyles.pill,
              active && scopeStyles.pillActive,
              pressed && { opacity: 0.85 },
            ]}
          >
            <Text
              style={[
                scopeStyles.pillText,
                active && scopeStyles.pillTextActive,
              ]}
            >
              {opt.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
};

const scopeStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 8,
  },
  label: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginRight: 4,
    fontFamily: 'Inter_500Medium',
  },
  pill: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surface,
  },
  pillActive: {
    borderColor: fpColors.primary,
    backgroundColor: fpColors.primaryContainer,
  },
  pillText: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    fontFamily: 'Inter_500Medium',
  },
  pillTextActive: {
    color: fpColors.primary,
    fontFamily: 'Inter_700Bold',
  },
});

/**
 * Memoized search bar. Owns the keystroke-by-keystroke local state
 * itself so typing never re-renders BrowseScreen or its FlatList — the
 * old setup hoisted `search` to the parent and made every keystroke
 * invalidate the entire list (root cause of the "second char feels
 * hung" lag the user reported). After 300 ms of idle, the local query
 * is dispatched into the filter slice; the parent's effect on
 * `filters.search` then fires the abortable refetch.
 */
const SearchBar: React.FC = React.memo(() => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectFilters);
  const isLoading = useAppSelector(selectBrowseLoading);
  const activeFiltersCount = useAppSelector(selectActiveFiltersCount);

  const externalSearch = filters.search ?? '';
  const [query, setQuery] = useState(externalSearch);
  // Search-UX §4.4 — track focus so the RecentSearchesDropdown only
  // renders when the input is focused AND empty.  Delay the blur by
  // a frame so taps on a row register before the dropdown unmounts.
  const [isFocused, setIsFocused] = useState(false);

  // Debounce local typing into Redux. 300 ms balances "feels instant"
  // against the backend's 0.5–2.5 s response time — shorter would
  // pile up requests; the abort logic in the parent's fetch effect
  // already cancels stragglers, but every avoided request is a win.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((filters.search ?? '') !== query) {
        dispatch(setFilters({ ...filters, search: query }));
        // Search-UX §4.4 — also drop the committed query into
        // the recent-searches list.  The slice's `applyAddRecent`
        // de-dupes case-insensitively and caps at 8 with FIFO
        // eviction, so this is safe to fire on every commit.
        if (query.trim()) dispatch(addRecent(query));
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Sync local state when something *outside* SearchBar clears the
  // search (e.g., the empty-state "Clear search" button). Only sync on
  // empty so we never fight the user mid-typing.
  useEffect(() => {
    if (externalSearch === '' && query !== '') setQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSearch]);

  const showSpinner = isLoading && query.length > 0;

  // Search-UX §4.2 — voice mic.  Tap → recognize one phrase → set
  // the transcript as the query (the existing 300ms debounce above
  // then commits it to Redux).  Hide the mic entirely on platforms
  // that don't expose the Web Speech API (Firefox, native RN)
  // rather than show a disabled control.
  const { isSupported: voiceSupported, isListening, start: startVoice, stop: stopVoice } =
    useSpeechToText({
      onResult: (transcript) => setQuery(transcript),
      onError: () => {
        /* No toast for now — failing silently feels less intrusive
           than a popup; the icon snapping back to idle is the
           feedback.  Revisit if real users get confused. */
      },
    });
  const handleMicPress = useCallback(() => {
    if (isListening) stopVoice();
    else startVoice();
  }, [isListening, startVoice, stopVoice]);

  const handleClearAll = useCallback(() => {
    dispatch(
      setFilters({
        search: '',
        category: undefined,
        is_verified: undefined,
        min_value: undefined,
        max_value: undefined,
      }),
    );
    setQuery('');
  }, [dispatch]);

  return (
    <View style={styles.searchWrap}>
      <View style={styles.searchInputWrap}>
        <MaterialIcons name="search" size={20} color={fpColors.outline} />
        <TextInput
          value={query}
          onChangeText={setQuery}
          onFocus={() => setIsFocused(true)}
          // Delay blur ~120ms so dropdown row taps register before
          // the dropdown unmounts.  Same trick the typeahead
          // components use.
          onBlur={() => {
            setTimeout(() => setIsFocused(false), 120);
          }}
          placeholder="Search your inventory"
          placeholderTextColor={`${fpColors.outline}99`}
          style={styles.searchInput}
          returnKeyType="search"
        />
        {showSpinner ? (
          <ActivityIndicator size="small" color={fpColors.primary} />
        ) : query.length > 0 ? (
          <Pressable onPress={() => setQuery('')} hitSlop={6}>
            <MaterialIcons
              name="close"
              size={18}
              color={fpColors.outline}
            />
          </Pressable>
        ) : voiceSupported ? (
          // Search-UX §4.2 — voice mic.  Only renders when no query
          // is active (otherwise the clear-X takes the slot — the
          // user's mid-typing intent wins).  And only when the
          // runtime actually exposes Web Speech (Firefox / native
          // hide the button rather than show a disabled control).
          <Pressable
            onPress={handleMicPress}
            hitSlop={6}
            accessibilityRole="button"
            accessibilityLabel={
              isListening ? 'Stop voice search' : 'Search by voice'
            }
            testID="search-voice-mic"
          >
            <MaterialIcons
              name={isListening ? 'mic' : 'mic-none'}
              size={18}
              color={isListening ? fpColors.primary : fpColors.outline}
            />
          </Pressable>
        ) : null}
      </View>
      {/* Search-UX §4.4 — Recent searches.  Renders only when the
          input has focus AND the query is empty (mid-typing replaces
          the dropdown with the results list).  Self-hides when there
          are no recents to show. */}
      {isFocused && query.length === 0 && (
        <RecentSearchesDropdown onPick={(picked) => setQuery(picked)} />
      )}
      {activeFiltersCount > 0 && (
        <Pressable
          onPress={handleClearAll}
          style={styles.clearBtn}
          hitSlop={6}
          accessibilityRole="button"
          accessibilityLabel="Clear all filters"
        >
          {/* No leading X icon — the search input has its own clear-X
              and a second X right next to it read as one button
              overlapping another. The pill background + label is
              enough affordance. */}
          <Text style={styles.clearText}>Clear filters</Text>
        </Pressable>
      )}
    </View>
  );
});
SearchBar.displayName = 'SearchBar';

/**
 * Memoized list header. Pulled out of an inline `() => (...)` so the
 * FlatList doesn't re-render the whole title + stats + chips block on
 * every parent re-render (which happens on every Redux filter change).
 * Stable callback props keep memo equality cheap.
 */
type BrowseListHeaderProps = {
  totalCount: number;
  inventorySummary: ReturnType<typeof selectInventorySummaryStats>;
  netWorth: ReturnType<typeof selectNetWorthStats>;
  categoriesLength: number;
  categoryChips: string[];
  activeCategory: string | undefined;
  isDesktop: boolean;
  onAdd: () => void;
  onSelectCategory: (category: string | undefined) => void;
};

const BrowseListHeader: React.FC<BrowseListHeaderProps> = React.memo(
  ({
    totalCount,
    inventorySummary,
    netWorth,
    categoriesLength,
    categoryChips,
    activeCategory,
    isDesktop,
    onAdd,
    onSelectCategory,
  }) => (
    <View style={styles.headerBlock}>
      <View style={styles.titleRow}>
        <View style={{ flex: 1 }}>
          <Text style={styles.h1}>Your Items</Text>
          <Text style={styles.h1Meta}>
            {totalCount > 0
              ? `${totalCount.toLocaleString()} ${totalCount === 1 ? 'item' : 'items'} catalogued`
              : 'Your household inventory at a glance'}
          </Text>
        </View>
        <Pressable
          onPress={onAdd}
          style={styles.headerCTA}
          hitSlop={6}
          accessibilityRole="button"
          accessibilityLabel="Add new item"
        >
          <MaterialIcons name="add" size={18} color={fpColors.onPrimary} />
        </Pressable>
      </View>

      <StatsHeader
        totalItems={inventorySummary?.total_items ?? totalCount}
        totalValue={netWorth?.total_estimated_value ?? 0}
        categoriesCount={inventorySummary?.categories_count ?? categoriesLength}
        verifiedPct={inventorySummary?.completion_percentage ?? 0}
        isDesktop={isDesktop}
      />

      {/* FE #255 — SearchBar was here.  It now renders once at the
          BrowseScreen root above the layout-conditional render so EVERY
          layout sees it (not just Recent).  See the comment at the
          hoisted mount point. */}

      {categoryChips.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.chipRow}
        >
          <CategoryChip
            label="All"
            active={!activeCategory}
            onPress={() => onSelectCategory(undefined)}
          />
          {categoryChips.map((c) => (
            <CategoryChip
              key={String(c)}
              label={String(c)}
              active={activeCategory === String(c)}
              onPress={() => onSelectCategory(String(c))}
            />
          ))}
        </ScrollView>
      )}
    </View>
  ),
);
BrowseListHeader.displayName = 'BrowseListHeader';


const formatCompactValue = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 10_000) return `$${Math.round(n / 1_000)}K`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const StatsHeader: React.FC<{
  totalItems: number;
  totalValue: number;
  categoriesCount: number;
  verifiedPct: number;
  isDesktop: boolean;
}> = ({ totalItems, totalValue, categoriesCount, verifiedPct, isDesktop }) => {
  const stats: {
    icon: keyof typeof MaterialIcons.glyphMap;
    value: string;
    label: string;
    tone: string;
  }[] = [
    {
      icon: 'inventory',
      value: totalItems.toLocaleString(),
      label: 'Items',
      tone: fpColors.primary,
    },
    {
      icon: 'attach-money',
      value: formatCompactValue(totalValue),
      label: 'Total value',
      tone: fpColors.secondary,
    },
    {
      icon: 'category',
      value: String(categoriesCount),
      label: 'Categories',
      tone: fpColors.tertiary,
    },
    {
      icon: 'verified',
      value: `${Math.round(verifiedPct)}%`,
      label: 'Verified',
      tone: fpColors.primary,
    },
  ];
  return (
    <View style={[styles.statsGrid, isDesktop && styles.statsGridDesktop]}>
      {stats.map((s) => (
        <View key={s.label} style={[styles.statCard, isDesktop && styles.statCardDesktop]}>
          <View style={[styles.statIconWrap, { backgroundColor: `${s.tone}1a` }]}>
            <MaterialIcons name={s.icon} size={16} color={s.tone} />
          </View>
          <Text style={styles.statValue}>{s.value}</Text>
          <Text style={styles.statLabel}>{s.label}</Text>
        </View>
      ))}
    </View>
  );
};

const CategoryChip: React.FC<{
  label: string;
  active: boolean;
  onPress: () => void;
}> = ({ label, active, onPress }) => (
  <Pressable
    onPress={onPress}
    style={[styles.chip, active && styles.chipActive]}
  >
    <Text style={[styles.chipLabel, active && styles.chipLabelActive]}>
      {label}
    </Text>
  </Pressable>
);

type ItemGridCardProps = {
  item: EnhancedInventoryItem;
  isDeleting: boolean;
  /** PR11 — when this item was claimed via a share link, the matched
   *  share's current status. `RETURNED` / `COMPLETED` greys the card +
   *  renders a "Returned" badge. `CLAIMED` (live borrow) and `null`
   *  (not a share-claim row, or share not yet hydrated) render normally.
   */
  claimedShareStatus?: 'CLAIMED' | 'RETURNED' | 'COMPLETED' | null;
  /** FE-204 — the viewer's user id, used to derive borrow/lend state
   *  from the row's `user_id` (owner) and `current_custodian_user_id`
   *  (current holder). Null when no user is signed in. */
  viewerUserId?: number | null;
  /** FE-204 — display name resolver for the counterparty (the owner
   *  when viewer is borrower, or the custodian when viewer is owner).
   *  Returns null when the id isn't in any cached group_member list,
   *  in which case the card falls back to a generic "a crew member"
   *  string. */
  resolveUserName?: (userId: number) => string | null;
  onPress: () => void;
  onDelete: () => void;
};

const ItemGridCard: React.FC<ItemGridCardProps> = React.memo(
  ({
    item,
    isDeleting,
    claimedShareStatus,
    viewerUserId,
    resolveUserName,
    onPress,
    onDelete,
  }) => {
    const dispatch = useAppDispatch();
    // FE-256 — image fallback chain moved into <ItemThumbnail>. The
    // local `image` variable stays for downstream code that still
    // needs to know "do we have a photo at all?" (e.g., conditional
    // overlays). When more callers stop checking this, drop it.
    const image =
      item.primary_photo_url ||
      item.source_image_url ||
      (item as any).image_url;
    const value = (item as any).estimated_value;
    const isVerified = (item as any).is_verified === true;
    const itemId = (item as any).id;
    const [verifying, setVerifying] = useState(false);

    // Sprint 8 follow-up — verify-chip replaces the standalone Verify
    // tab. Tap on an unverified card's left badge fires `PATCH
    // /users/inventory/{id}` with `is_verified=true`. Same handler
    // logic the dropped VerifyScreen used, scoped down to a single
    // card so users can fix one at a time without context-switching.
    const handleVerifyTap = useCallback(
      async (e?: any) => {
        // stop card press from also navigating to ItemDetail
        e?.stopPropagation?.();
        if (!itemId || isVerified || verifying) return;
        setVerifying(true);
        try {
          try {
            const updated = await api.updateInventoryItem(itemId, {
              is_verified: true,
            } as any);
            dispatch(mergeInventoryItem(updated));
          } catch {
            // Backend may silently ignore the field today — still
            // merge locally so the chip flips immediately.
            dispatch(
              mergeInventoryItem({ ...item, is_verified: true } as any),
            );
          }
          Toast.show({
            type: 'success',
            text1: 'Verified',
            text2: item.item_name ?? 'Item marked verified',
          });
        } finally {
          setVerifying(false);
        }
      },
      [dispatch, item, itemId, isVerified, verifying],
    );

    // PR11 — share-claim rows that have been returned (or fully
    // completed) get de-emphasized: lower opacity on the photo + a
    // "Returned" badge in the corner. The user still sees the row
    // (so they don't think the item vanished), but it visually
    // recedes from the active inventory.
    const isReturnedShareClaim =
      claimedShareStatus === 'RETURNED' || claimedShareStatus === 'COMPLETED';

    // FE-204 — possession-vs-ownership pill state.
    //
    //   - "Borrowed from X" → viewer is the CUSTODIAN of an item
    //     they don't own. This case will primarily arrive after
    //     BE-210 ships `held_by_me=true` on /users/inventory; until
    //     then the card never renders because the row isn't in the
    //     grid query result. The code path is wired here so it
    //     lights up automatically when the backend lands.
    //
    //   - "Lent to X" → viewer is the OWNER and a different user is
    //     currently the custodian. Works TODAY (the row IS in the
    //     grid because user_id == viewer; the custodian column tells
    //     us it's out). Lower-key pill — informational, not a state
    //     change like share-claim-returned.
    const possession = deriveItemPossessionState({
      viewerUserId,
      ownerUserId: (item as any).user_id as number | null | undefined,
      custodianUserId: (item as any).current_custodian_user_id as
        | number
        | null
        | undefined,
      // FE-248 v2 follow-up — fall back to the BE's `is_borrowed`
      // flag when the canonical custody ids are absent. Without this,
      // the same logical state ("I'm holding it but don't own it")
      // produced different pill copy on different cards depending on
      // which BE path created the custody. Tracked as backend#248.
      isBorrowedFlag:
        (item as any).sharing_info?.is_borrowed === true,
    });
    const viewerIsBorrower = possession.kind === 'BORROWED';
    const ownerLentOut = possession.kind === 'LENT_OUT';
    const counterpartyName =
      possession.kind !== 'NONE' &&
      resolveUserName &&
      // Sentinel -1 means "BE didn't tell us who the owner is" (the
      // is_borrowed fallback path). Don't try to resolve a name.
      possession.counterpartyUserId !== -1
        ? resolveUserName(possession.counterpartyUserId)
        : null;

    return (
      <Pressable
        onPress={onPress}
        disabled={isDeleting}
        style={({ pressed }) => [
          styles.card,
          pressed && styles.cardPressed,
          isDeleting && styles.cardDeleting,
          isReturnedShareClaim && styles.cardReturned,
          // FE-204 — borrowed-via-crew gets the same grey treatment
          // as PR11's returned share-claim (it's "not really yours").
          viewerIsBorrower && styles.cardReturned,
        ]}
        // FE-258.3 — testID lets Playwright target a specific row by id.
        testID={`browse-item-row-${itemId}`}
      >
        <View style={styles.cardImageWrap}>
          {/* FE-256 — shared fallback chain. Overlay styles
              (cardImageReturned) still apply via the `style` prop;
              ItemThumbnail respects whatever sizing/opacity comes in. */}
          <ItemThumbnail
            item={item as any}
            style={[
              styles.cardImage,
              isReturnedShareClaim && styles.cardImageReturned,
              viewerIsBorrower && styles.cardImageReturned,
            ]}
          />
          {/* FE-204 — "Borrowed from X" pill. Same corner as
              PR11's Returned badge; mutually exclusive (a returned
              share-claim is never simultaneously a crew-borrow). */}
          {viewerIsBorrower && !isReturnedShareClaim ? (
            <View
              style={styles.cardBorrowedBadge}
              accessibilityLabel={
                counterpartyName
                  ? `Borrowed from ${counterpartyName}`
                  : 'Borrowed from another member'
              }
              testID={`browse-card-borrowed-${itemId}`}
            >
              <MaterialIcons
                name="south-west"
                size={10}
                color={fpColors.onPrimary}
              />
              <Text style={styles.cardBorrowedBadgeText}>
                {counterpartyName
                  ? `From ${counterpartyName}`
                  : 'Borrowed'}
              </Text>
            </View>
          ) : null}
          {/* FE-204 — "Lent to X" pill on owner's own-but-out items.
              Subtle: outlined pill (vs filled for borrowed) so the
              owner reads "I lent this" at a glance. */}
          {ownerLentOut && !isReturnedShareClaim ? (
            <View
              style={styles.cardLentOutBadge}
              accessibilityLabel={
                counterpartyName
                  ? `Lent to ${counterpartyName}`
                  : 'Lent to another member'
              }
              testID={`browse-card-lentout-${itemId}`}
            >
              <MaterialIcons
                name="north-east"
                size={10}
                color={fpColors.primary}
              />
              <Text style={styles.cardLentOutBadgeText}>
                {counterpartyName
                  ? `Lent to ${counterpartyName}`
                  : 'Lent out'}
              </Text>
            </View>
          ) : null}
          {isReturnedShareClaim ? (
            <View
              style={styles.cardReturnedBadge}
              accessibilityLabel={
                claimedShareStatus === 'COMPLETED'
                  ? 'Returned — receipt confirmed'
                  : 'Returned — awaiting confirmation'
              }
            >
              <MaterialIcons
                name={
                  claimedShareStatus === 'COMPLETED'
                    ? 'check-circle'
                    : 'schedule-send'
                }
                size={10}
                color={fpColors.onPrimary}
              />
              <Text style={styles.cardReturnedBadgeText}>Returned</Text>
            </View>
          ) : null}
          <Pressable
            onPress={onDelete}
            hitSlop={8}
            style={styles.cardDeleteBtn}
            accessibilityLabel="Delete item"
            accessibilityHint="Removes this item from your inventory"
          >
            {/* Gap #112 (2026-05-27 audit) — the X used to render in
                `fpColors.error` (red) at size 14 with a near-opaque
                white pill behind it. Two real users misread that as a
                cancel/clear of an in-flight state rather than a
                delete-now, AND the visual loudness of an always-red X
                on every card made the grid feel poised for deletion.
                De-emphasize the icon (smaller, outline gray) while
                keeping the same handler — the confirm dialog still
                gates the actual destructive call. Tap area widened
                slightly (hitSlop 6→8) to compensate for the smaller
                visual target. */}
            <MaterialIcons name="close" size={12} color={fpColors.outline} />
          </Pressable>
          {isVerified ? (
            <View
              style={styles.cardVerifiedBadge}
              accessibilityLabel="Verified"
            >
              <MaterialIcons
                name="verified"
                size={12}
                color={fpColors.onPrimary}
              />
            </View>
          ) : (
            <Pressable
              onPress={handleVerifyTap}
              hitSlop={6}
              disabled={verifying}
              style={({ pressed }) => [
                styles.cardVerifyChip,
                pressed && { opacity: 0.7 },
                verifying && { opacity: 0.6 },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Mark this item verified"
              testID={`browse-card-verify-${itemId}`}
            >
              {verifying ? (
                <ActivityIndicator size="small" color={fpColors.primary} />
              ) : (
                <>
                  <MaterialIcons
                    name="check"
                    size={11}
                    color={fpColors.primary}
                  />
                  <Text style={styles.cardVerifyChipText}>Verify</Text>
                </>
              )}
            </Pressable>
          )}
          {/* FE-248 v2 follow-up — removed standalone CustodyChip from
              Browse cards. It was producing a second pill on the same
              card ("You have" alongside "Borrowed from someone"); the
              cardBorrowedBadge above now covers every borrowed-by-me
              row via the unified deriveItemPossessionState (canonical
              custody fields + is_borrowed fallback). CustodyChip is
              still mounted on ItemDetail where the richer 5-state
              copy still serves a purpose. */}
        </View>
        <View style={styles.cardBody}>
          <Text style={styles.cardName} numberOfLines={1}>
            {item.item_name}
          </Text>
          {!!item.item_category && (
            <Text style={styles.cardCategory} numberOfLines={1}>
              {item.item_category}
            </Text>
          )}
          {typeof value === 'number' && (
            <Text style={styles.cardValue}>${value.toFixed(2)}</Text>
          )}
          {/* Sprint 2 multi-group chips (backend#111 Gap A — live on
              prod). Renders the first crew this item is bound to;
              if there are more, suffixes a "+N" overflow chip. The
              user's `PERSONAL` group is suppressed since it carries
              no signal — every item is in personal by default. */}
          <CrewChipsRow groups={(item as any).groups} />
        </View>
      </Pressable>
    );
  },
  // Custom equality: ignore onPress/onDelete identity (parent recreates
  // them per render) so cards only re-render when the item itself or
  // its deleting flag actually changes.
  (a, b) =>
    a.isDeleting === b.isDeleting &&
    (a.item as any).id === (b.item as any).id &&
    a.item.item_name === b.item.item_name &&
    a.item.primary_photo_url === b.item.primary_photo_url &&
    (a.item as any).estimated_value === (b.item as any).estimated_value &&
    (a.item as any).is_verified === (b.item as any).is_verified &&
    a.item.item_category === b.item.item_category &&
    // Sprint 4 / FE-10.5 — re-render when custody flips so the
    // overlay chip reflects the holder swap without a refetch.
    (a.item as any).current_custodian_user_id ===
      (b.item as any).current_custodian_user_id &&
    (a.item as any).custody_due_date === (b.item as any).custody_due_date &&
    // FE-204 — re-render when owner changes (rare but possible
    // post-transfer) so the borrower/lent-out derivation stays
    // correct.
    (a.item as any).user_id === (b.item as any).user_id &&
    // Sprint 8 / BE#160 — re-render when share-claim attribution
    // changes so the "Borrowed from X" pill reflects the new lender.
    (a.item as any).claimed_from_user_id ===
      (b.item as any).claimed_from_user_id &&
    // PR11 — re-render when the matched share's status flips
    // (CLAIMED → RETURNED → COMPLETED) so the card greys correctly.
    a.claimedShareStatus === b.claimedShareStatus &&
    // FE-204 — re-render when the viewer changes (auth refresh,
    // multi-account swap) or when the resolveUserName closure
    // identity changes (the parent rebuilds it whenever the
    // groupDetailsMap snapshot updates — new member loaded etc.).
    a.viewerUserId === b.viewerUserId &&
    a.resolveUserName === b.resolveUserName,
);
ItemGridCard.displayName = 'ItemGridCard';

/**
 * Sprint 2 / FE-9.4 — per-card multi-group chips. Reads
 * `InventoryItemResponse.groups[]` (now exposed per backend#111).
 * Skips PERSONAL (every item is there by default), shows the first
 * 1 crew + a "+N" overflow chip when more exist.
 */
const CrewChipsRow: React.FC<{ groups?: { id: number; name: string; kind: string }[] }> = ({
  groups,
}) => {
  if (!groups || groups.length === 0) return null;
  const real = groups.filter((g) => g.kind !== 'PERSONAL');
  if (real.length === 0) return null;
  const first = real[0];
  const more = real.length - 1;
  return (
    <View style={styles.crewChipsRow}>
      <View style={styles.crewChip}>
        <MaterialIcons name="group" size={10} color={fpColors.primary} />
        <Text style={styles.crewChipText} numberOfLines={1}>
          {first.name}
        </Text>
      </View>
      {more > 0 ? (
        <View style={[styles.crewChip, styles.crewChipMore]}>
          <Text style={styles.crewChipText}>{`+${more}`}</Text>
        </View>
      ) : null}
    </View>
  );
};

/* ————— styles ————— */

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  topBarRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onPrimaryContainer,
    letterSpacing: 0.5,
  },

  headerBlock: {
    paddingHorizontal: 16,
    paddingTop: 18,
    paddingBottom: 4,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 14,
  },
  h1: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 28,
    letterSpacing: -0.4,
    color: fpColors.onSurface,
  },
  h1Meta: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  headerCTA: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: fpColors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: fpColors.primary,
    shadowOpacity: 0.25,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 3,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 6,
  },
  statsGridDesktop: {
    flexWrap: 'nowrap',
  },
  statCard: {
    flexBasis: '48%',
    flexGrow: 1,
    minWidth: 140,
    borderRadius: 14,
    padding: 12,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 6,
  },
  statCardDesktop: {
    flexBasis: 0,
  },
  statIconWrap: {
    width: 30,
    height: 30,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statValue: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 20,
    color: fpColors.onSurface,
    letterSpacing: -0.3,
  },
  statLabel: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },

  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingTop: 14,
    paddingBottom: 4,
  },
  searchInputWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  searchInput: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    color: fpColors.onSurface,
    ...(({ outlineStyle: 'none', outlineWidth: 0 } as any)),
  },
  clearBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}14`,
  },
  clearText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.primary,
  },

  chipRow: {
    flexDirection: 'row',
    gap: 8,
    paddingTop: 8,
    paddingBottom: 12,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  chipActive: {
    backgroundColor: fpColors.primary,
    borderColor: fpColors.primary,
  },
  chipLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  chipLabelActive: {
    color: fpColors.onPrimary,
  },

  listContent: {
    paddingHorizontal: 16,
    paddingBottom: 32,
    gap: 10,
  },
  columnWrapper: {
    gap: 10,
  },

  // Chunk 9.5 / FE-11.5.A Stage 2A — Full-bleed map wrapper.
  // Fills the remaining viewport below the top bar + pills row.
  // The map itself is positioned `relative` so its inner control
  // stack + popups overlay correctly; the bottom nav (handled by
  // React Navigation's bottom tab bar) covers the bottom edge.
  fullBleedMap: {
    flex: 1,
    position: 'relative' as any,
  },

  // FE #255 — Padded wrapper for the hoisted SearchBar.  Mirrors the
  // headerBlock horizontal padding so the search input sits flush with
  // the rest of the chrome when Recent's BrowseListHeader is not
  // mounted (Snapshots / Location / Tag / Crew).
  hoistedSearchBar: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },

  card: {
    flex: 1,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    overflow: 'hidden',
    marginBottom: 10,
  },
  cardPressed: { opacity: 0.9 },
  cardDeleting: { opacity: 0.4 },
  // PR11 — share-claim row that's been returned/completed. Card stays
  // tappable (user might want to view custody history) but visually
  // recedes so it doesn't compete with active inventory.
  cardReturned: { opacity: 0.55 },
  cardImageWrap: {
    position: 'relative',
    width: '100%',
    aspectRatio: 1,
    backgroundColor: fpColors.surfaceContainer,
  },
  cardImage: { width: '100%', height: '100%' },
  // PR11 — further mute the photo (in addition to cardReturned wrapper
  // opacity) so the photo reads "archived" at a glance.
  cardImageReturned: { opacity: 0.75 },
  cardImagePlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  // PR11 — "Returned" pill in the photo's bottom-left corner. Sits
  // opposite the CustodyChip (bottom-right) so they don't collide.
  cardReturnedBadge: {
    position: 'absolute',
    bottom: 6,
    left: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: fpColors.tertiary,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 2,
    elevation: 2,
  },
  cardReturnedBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: fpColors.onPrimary,
    letterSpacing: 0.2,
  },
  // FE-204 — "Borrowed from X" pill (filled primary). Same corner
  // as cardReturnedBadge but only one of the two ever renders at
  // once (mutually exclusive states).
  cardBorrowedBadge: {
    position: 'absolute',
    bottom: 6,
    left: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: fpColors.primary,
    maxWidth: '85%',
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 2,
    elevation: 2,
  },
  cardBorrowedBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: fpColors.onPrimary,
    letterSpacing: 0.2,
  },
  // FE-204 — "Lent to X" pill (outline, low-key). Different style
  // from Borrowed because for the owner this is informational, not
  // a "you don't really own this" signal.
  cardLentOutBadge: {
    position: 'absolute',
    bottom: 6,
    left: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderWidth: 1,
    borderColor: fpColors.primary,
    maxWidth: '85%',
  },
  cardLentOutBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: fpColors.primary,
    letterSpacing: 0.2,
  },
  cardDeleteBtn: {
    // Gap #112 — de-emphasized: smaller pill, less-opaque bg, no
    // shadow. The icon inside is also smaller + outline-gray now.
    position: 'absolute',
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: 'rgba(255,255,255,0.72)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardVerifiedBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: fpColors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Sprint 8 follow-up — tap-to-verify chip replaces the dropped
  // Verify tab. Lives in the same corner as the verified-check badge
  // so the visual swap on tap is in-place (chip → check). Uses an
  // outlined treatment so it reads as "action available" instead of
  // "status achieved".
  cardVerifyChip: {
    position: 'absolute',
    top: 6,
    left: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.94)',
    borderWidth: 1,
    borderColor: fpColors.primary,
    minHeight: 22,
  },
  cardVerifyChipText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: fpColors.primary,
    letterSpacing: 0.2,
  },
  cardBody: {
    padding: 10,
    gap: 2,
  },
  cardName: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  cardCategory: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
  cardValue: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.primary,
    marginTop: 2,
  },

  // FE-9.4 — multi-group chips on item cards
  crewChipsRow: {
    flexDirection: 'row',
    gap: 4,
    marginTop: 6,
    flexWrap: 'wrap',
  },
  crewChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: `${fpColors.primary}1a`, // primary @ 10%
    maxWidth: '85%',
  },
  crewChipMore: {
    backgroundColor: fpColors.surfaceContainer,
  },
  crewChipText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: fpColors.primary,
    maxWidth: 80,
  },

  footer: {
    paddingVertical: 16,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  footerText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.outline,
  },
  empty: {
    padding: 36,
    alignItems: 'center',
    gap: 10,
  },
  emptyTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
    marginTop: 6,
  },
  emptyMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    maxWidth: 320,
  },
  emptyCTA: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 12,
    backgroundColor: fpColors.primary,
    marginTop: 10,
  },
  emptyCTAText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onPrimary,
  },
});

/* ────────────────────────────────────────────────────────────────────
 * BrowseSectionedView — Sprint 2 (FE-9.4).
 *
 * Renders the Scenes / Location / Crew / Tag layouts. Each layout is
 * a series of section headers + horizontal item strips (or vertical
 * grids — depends on the section's content).
 *
 * For pragma + scope, this view doesn't paginate per-section. The
 * Recent layout (existing FlatList) handles pagination; sectioned
 * views render the items the user has already loaded. Sprint 5
 * polish can add per-section pagination if needed.
 * ──────────────────────────────────────────────────────────────────── */

interface BrowseSectionedViewProps {
  sections: BrowseSection[];
  isLoading: boolean;
  onRefresh: () => void;
  onItemPress: (item: EnhancedInventoryItem) => void;
  onItemDelete: (item: EnhancedInventoryItem) => void;
  onCrewPress: (groupId: number) => void;
  /** Sprint 3 (FE-11.5.E) — scene-hero tap → SceneViewerScreen. */
  onScenePress?: (fileId: number) => void;
  /** FE-210 — ⋮ tap on a scene-header → DeleteSceneSheet. */
  onSceneDeletePress?: (fileId: number) => void;
  deletingIds: string[];
  numColumns: number;
  mode: BrowseLayout;
  /** Sprint 2.5 (FE-9.4.1) — scenes pagination state. Only used when
   *  `mode === 'scenes'`. When `hasMore` is true and user taps the
   *  footer, `onLoadMoreScenes()` fires the next page request. */
  scenesPagination?: {
    total: number;
    loaded: number;
    hasMore: boolean;
    loading: boolean;
  };
  onLoadMoreScenes?: () => void;
  /** FE #255 — active search query from `filters.search`.  Lets the
   *  scenes-mode empty state explain "No scenes match \"{query}\""
   *  instead of the generic "Nothing to group by scenes" copy, which
   *  is confusing when the user is staring at items they know they
   *  own and just typed a query that didn't match. */
  searchQuery?: string;
}

const BrowseSectionedView: React.FC<BrowseSectionedViewProps> = ({
  sections,
  isLoading,
  onRefresh,
  onItemPress,
  onItemDelete,
  onCrewPress,
  onScenePress,
  onSceneDeletePress,
  deletingIds,
  numColumns: _numColumns,
  mode,
  scenesPagination,
  onLoadMoreScenes,
  searchQuery,
}) => {
  // Sprint 3 (FE-11.5.C) — ref to FlatList so the FeaturedTagsStrip
  // can scrollToIndex when a chip is tapped.
  const flatListRef = useRef<FlatList<BrowseSection> | null>(null);
  if (sections.length === 0) {
    // FE #255 — distinguish "no data" from "search has no matches".
    // Without this, a Snapshots user who types `kitchen` and gets zero
    // hits sees "Nothing to group by scenes" which reads like "you
    // have no scenes" — wrong + confusing.
    const q = (searchQuery ?? '').trim();
    const isFilteredEmpty = q.length > 0 && mode === 'scenes';
    return (
      <View style={styles.empty}>
        {isLoading ? (
          <ActivityIndicator size="small" color={fpColors.primary} />
        ) : isFilteredEmpty ? (
          <>
            <MaterialIcons name="search-off" size={48} color={fpColors.outline} />
            <Text style={styles.emptyTitle}>
              {`No scenes match “${q}”`}
            </Text>
            <Text style={styles.emptyMeta}>
              Clear the search above to see every snapshot, or try a different query.
            </Text>
          </>
        ) : (
          <>
            <MaterialIcons
              name={mode === 'crew' ? 'groups' : 'inventory'}
              size={48}
              color={fpColors.outline}
            />
            <Text style={styles.emptyTitle}>
              {mode === 'crew'
                ? 'No circles yet'
                : `Nothing to group by ${mode}`}
            </Text>
            <Text style={styles.emptyMeta}>
              {mode === 'crew'
                ? 'Create a circle first, then your items will surface here.'
                : 'Add items or assign them to surface here.'}
            </Text>
          </>
        )}
      </View>
    );
  }

  return (
    <FlatList
      data={sections}
      keyExtractor={(s) => s.key}
      contentContainerStyle={styles.listContent}
      refreshControl={
        <RefreshControl
          refreshing={isLoading && sections.length === 0}
          onRefresh={onRefresh}
          tintColor={fpColors.primary}
        />
      }
      ListHeaderComponent={
        // Sprint 2.5 (FE-9.4.2) — explanatory banners for layouts
        // whose data sources are typically sparse on backfilled
        // accounts. Sprint 3 (FE-11.5.C) adds the featured-tags
        // strip on tag mode for tap-to-jump navigation.
        mode === 'location' ? (
          <SectionInfoBanner
            text="Grouped by the item's free-text location field. Switch to Map above to plot items by their scan GPS instead."
          />
        ) : mode === 'tag' ? (
          // FE #257 — when the TagTypeahead has chips active the
          // section list collapses to one AND-intersection section
          // keyed `tag-filter-{a+b+...}`.  Scrolling to a per-tag
          // section doesn't make sense in that mode, so hide the
          // FeaturedTagsStrip + the "Tag your items..." banner —
          // the chip row at the top is the affordance now.
          sections.length === 1 &&
          sections[0].key.startsWith('tag-filter-') ? null : (
            <View>
              <FeaturedTagsStrip
                sections={sections}
                onChipPress={(sectionKey) => {
                  if (!flatListRef.current) return;
                  const idx = sections.findIndex((s) => s.key === sectionKey);
                  if (idx < 0) return;
                  try {
                    flatListRef.current.scrollToIndex({
                      index: idx,
                      animated: true,
                      viewPosition: 0,
                    });
                  } catch {
                    /* If scrollToIndex fails (e.g. item not yet measured),
                       fall back to scrollToOffset with an estimate. */
                  }
                }}
              />
              <SectionInfoBanner
                text="Tag your items from ItemDetail to surface them here."
              />
            </View>
          )
        ) : null
      }
      ref={flatListRef}
      renderItem={({ item: section }) => (
        <BrowseSectionRow
          section={section}
          onItemPress={onItemPress}
          onItemDelete={onItemDelete}
          onCrewPress={onCrewPress}
          onScenePress={onScenePress}
          onSceneDeletePress={onSceneDeletePress}
          deletingIds={deletingIds}
          mode={mode}
        />
      )}
      ListFooterComponent={
        mode === 'scenes' && scenesPagination ? (
          <ScenesLoadMoreFooter
            pagination={scenesPagination}
            onLoadMore={onLoadMoreScenes}
          />
        ) : null
      }
    />
  );
};

/* Sprint 2.5 (FE-9.4.2) — small ⓘ banner shown above the section
 * list on layouts whose data sources are typically sparse on
 * backfilled inventory (Location, Tag). Sets correct expectations
 * for users who don't have these fields populated. */
const SectionInfoBanner: React.FC<{ text: string }> = ({ text }) => (
  <View style={sectionBannerStyles.wrap}>
    <MaterialIcons
      name="info-outline"
      size={16}
      color={fpColors.primary}
      style={{ marginRight: 8 }}
    />
    <Text style={sectionBannerStyles.text}>{text}</Text>
  </View>
);

const sectionBannerStyles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginHorizontal: 16,
    marginBottom: 12,
    backgroundColor: '#fff8fd', // primary-container tinted
    borderRadius: 8,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  text: {
    flex: 1,
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    lineHeight: 16,
  },
});

/* Sprint 2.5 (FE-9.4.1) — pagination footer for the Scenes layout.
 * Three states:
 *   - hasMore + !loading → tappable "Load N more scenes" button
 *   - loading             → spinner
 *   - !hasMore + total>0  → "Showing X of Y scenes" tail
 */
interface ScenesLoadMoreFooterProps {
  pagination: NonNullable<BrowseSectionedViewProps['scenesPagination']>;
  onLoadMore?: () => void;
}

const ScenesLoadMoreFooter: React.FC<ScenesLoadMoreFooterProps> = ({
  pagination,
  onLoadMore,
}) => {
  const { total, loaded, hasMore, loading } = pagination;

  if (loading) {
    return (
      <View style={scenesFooterStyles.footer}>
        <ActivityIndicator color={fpColors.primary} />
      </View>
    );
  }

  if (hasMore) {
    const remaining = Math.max(0, total - loaded);
    const next = Math.min(20, remaining);
    return (
      <Pressable
        onPress={onLoadMore}
        accessibilityRole="button"
        accessibilityLabel="Load more snapshots"
        style={({ pressed }) => [
          scenesFooterStyles.button,
          pressed && { opacity: 0.85 },
        ]}
      >
        <Text style={scenesFooterStyles.buttonText}>
          {`Load ${next} more snapshot${next === 1 ? '' : 's'}`}
        </Text>
      </Pressable>
    );
  }

  if (total > 0) {
    return (
      <Text style={scenesFooterStyles.tail}>
        {`Showing ${loaded} of ${total} snapshot${total === 1 ? '' : 's'}`}
      </Text>
    );
  }

  return null;
};

const scenesFooterStyles = StyleSheet.create({
  footer: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  button: {
    marginTop: 12,
    marginHorizontal: 16,
    marginBottom: 24,
    paddingVertical: 14,
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
    color: fpColors.primary,
  },
  tail: {
    textAlign: 'center',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    paddingVertical: 16,
  },
});

interface BrowseSectionRowProps {
  section: BrowseSection;
  onItemPress: (item: EnhancedInventoryItem) => void;
  onItemDelete: (item: EnhancedInventoryItem) => void;
  onCrewPress: (groupId: number) => void;
  /** Sprint 3 (FE-11.5.E) — fired when user taps a scene's hero
   *  photo. fileId maps to FileWithInventoryResponse.id. */
  onScenePress?: (fileId: number) => void;
  /** FE-210 — fired when user taps the ⋮ on a scene-header. The
   *  parent opens DeleteSceneSheet with this file's metadata. */
  onSceneDeletePress?: (fileId: number) => void;
  deletingIds: string[];
  mode: BrowseLayout;
}

const BrowseSectionRow: React.FC<BrowseSectionRowProps> = ({
  section,
  onItemPress,
  onItemDelete: _onItemDelete,
  onCrewPress,
  onScenePress,
  onSceneDeletePress,
  deletingIds: _deletingIds,
  mode,
}) => {
  // Crew section: header is tappable; FE-9.4.3 adds a 5-thumbnail
  // strip pulled from the per-crew inventory cache (FE-9.3 wired this
  // for GroupDetailScreen; we reuse the same store here).
  if (mode === 'crew') {
    return (
      <CrewSectionRow
        section={section}
        onCrewPress={onCrewPress}
      />
    );
  }

  // Scenes / Location / Tag: header + horizontal item strip.
  // FE-210 — Scenes mode gets a ⋮ menu button in the header for
  // "Delete scene" with cascade option. Other layouts don't (their
  // "section" is a derived grouping, not a deletable backend row).
  const sceneFileId =
    mode === 'scenes'
      ? Number((section.key.match(/^scene-(\d+)/) ?? [])[1])
      : null;

  return (
    <View style={sectionedStyles.section}>
      <View style={sectionedStyles.sectionHeader}>
        <View style={sectionedStyles.sectionHeaderRow}>
          <View style={{ flex: 1 }}>
            <Text style={sectionedStyles.sectionTitle} numberOfLines={1}>
              {section.title}
            </Text>
            {section.subtitle && (
              <Text style={sectionedStyles.sectionSubtitle}>
                {section.subtitle}
              </Text>
            )}
          </View>
          {mode === 'scenes' &&
          sceneFileId != null &&
          Number.isFinite(sceneFileId) &&
          onSceneDeletePress ? (
            <Pressable
              onPress={() => onSceneDeletePress(sceneFileId)}
              accessibilityRole="button"
              accessibilityLabel={`Delete snapshot ${section.title}`}
              hitSlop={8}
              testID={`scene-delete-${sceneFileId}`}
              style={({ pressed }) => [
                sectionedStyles.overflowBtn,
                pressed && { opacity: 0.8 },
              ]}
            >
              <MaterialIcons
                name="delete-outline"
                size={20}
                color={fpColors.onSurfaceVariant}
              />
            </Pressable>
          ) : null}
        </View>
      </View>
      {/* Scenes hero photo — Sprint 3 (FE-11.5.E) makes it tappable
          to open the read-only SceneViewerScreen. The fileId is
          extracted from `section.key` (format: "scene-${file.id}"). */}
      {mode === 'scenes' && section.heroPhotoUrl && (
        <Pressable
          onPress={() => {
            if (!onScenePress) return;
            const m = section.key.match(/^scene-(\d+)/);
            if (!m) return;
            onScenePress(Number(m[1]));
          }}
          accessibilityRole="button"
          accessibilityLabel={`Open snapshot ${section.title}`}
          style={({ pressed }) => [
            { opacity: pressed ? 0.92 : 1 },
          ]}
        >
          <Image
            source={{ uri: section.heroPhotoUrl }}
            style={sectionedStyles.sceneHero}
            accessibilityIgnoresInvertColors
          />
        </Pressable>
      )}
      {/* Horizontal thumbnail strip — taps open ItemDetail. */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={sectionedStyles.thumbStrip}
      >
        {section.data.slice(0, 12).map((it) => (
          <Pressable
            key={`${section.key}-${it.id}`}
            onPress={() => onItemPress(it)}
            accessibilityRole="button"
            accessibilityLabel={it.item_name}
            style={({ pressed }) => [
              sectionedStyles.thumbWrap,
              pressed && { opacity: 0.85 },
            ]}
          >
            {it.primary_photo_url || it.source_image_url ? (
              <Image
                source={{
                  uri: (it.primary_photo_url || it.source_image_url) as string,
                }}
                style={sectionedStyles.thumb}
                accessibilityIgnoresInvertColors
              />
            ) : (
              <View
                style={[sectionedStyles.thumb, sectionedStyles.thumbPlaceholder]}
              >
                <MaterialIcons name="image" size={20} color={fpColors.outline} />
              </View>
            )}
            <Text
              style={sectionedStyles.thumbLabel}
              numberOfLines={1}
            >
              {it.item_name}
            </Text>
          </Pressable>
        ))}
        {section.data.length > 12 && (
          <View style={sectionedStyles.overflow}>
            <Text style={sectionedStyles.overflowText}>
              +{section.data.length - 12} more
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const sectionedStyles = StyleSheet.create({
  section: {
    marginBottom: 16,
  },
  sectionHeader: {
    paddingHorizontal: 16,
    marginBottom: 6,
  },
  // FE-210 — flex row inside the header so the trash button sits at
  // the far right, vertically centered against the title.
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  overflowBtn: {
    padding: 6,
    borderRadius: 8,
  },
  sectionTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  sectionSubtitle: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  sceneHero: {
    height: 120,
    marginHorizontal: 16,
    borderRadius: 8,
    backgroundColor: fpColors.surfaceContainerLow,
    marginBottom: 8,
  },
  thumbStrip: {
    paddingHorizontal: 16,
    gap: 8,
    paddingVertical: 4,
  },
  thumbWrap: {
    width: 84,
  },
  thumb: {
    width: 84,
    height: 84,
    borderRadius: 8,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  thumbPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbLabel: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    marginTop: 4,
  },
  overflow: {
    width: 84,
    height: 84,
    borderRadius: 8,
    backgroundColor: fpColors.surfaceContainerLow,
    alignItems: 'center',
    justifyContent: 'center',
  },
  overflowText: {
    fontSize: 12,
    fontWeight: '600',
    color: fpColors.primary,
  },
  crewCard: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 16,
    marginBottom: 10,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  crewHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  crewName: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
    flex: 1,
  },
  crewSubtitle: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 4,
    marginLeft: 28,
  },
  crewCTA: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: 6,
    gap: 2,
  },
  crewCTAText: {
    fontSize: 12,
    fontWeight: '600',
    color: fpColors.primary,
  },
});

/* ────────────────────────────────────────────────────────────────────
 * CrewSectionRow — Sprint 2.5 (FE-9.4.3).
 *
 * Renders a single crew section in the Crew layout. Header + 5-
 * thumbnail strip + "Open crew →" CTA in one tappable card.
 *
 * Pulls thumbnails from `groupsSlice.inventory[crewId]` (populated by
 * the parallel `loadGroupInventory({limit:5})` dispatches fired in
 * BrowseScreen's useEffect). Loading + empty + populated states all
 * render gracefully:
 *   - Loading: skeleton row of 5 placeholder boxes
 *   - 0 items: subtitle reads "No items yet" + still navigable
 *   - 1+ items: thumbnail strip + "+N more →" overflow chip
 * ──────────────────────────────────────────────────────────────────── */

interface CrewSectionRowProps {
  section: BrowseSection;
  onCrewPress: (groupId: number) => void;
}

const CrewSectionRow: React.FC<CrewSectionRowProps> = ({ section, onCrewPress }) => {
  const groupId = section.href?.kind === 'group' ? section.href.groupId : -1;
  const items = useAppSelector(selectGroupInventoryItems(groupId));
  const cache = useAppSelector(
    (s: any) => s.groups?.inventory?.[groupId],
  );
  const isPersonal = section.key === 'crew--1' || section.title === 'Personal';
  const isLoading = !!cache?.loading && items.length === 0;
  const total = cache?.total ?? items.length;

  return (
    <Pressable
      onPress={() => {
        if (groupId > 0) onCrewPress(groupId);
      }}
      accessibilityRole="button"
      accessibilityLabel={`Open ${section.title}`}
      style={({ pressed }) => [
        sectionedStyles.crewCard,
        pressed && { opacity: 0.86 },
      ]}
    >
      <View style={sectionedStyles.crewHeaderRow}>
        <MaterialIcons
          name={isPersonal ? 'person' : 'groups'}
          size={20}
          color={fpColors.primary}
        />
        <Text style={sectionedStyles.crewName} numberOfLines={1}>
          {section.title}
        </Text>
      </View>
      {section.subtitle && (
        <Text style={sectionedStyles.crewSubtitle}>{section.subtitle}</Text>
      )}

      {/* FE-9.4.3 — 5-thumbnail preview strip */}
      {(isLoading || items.length > 0) && (
        <View style={crewRowStyles.thumbStrip}>
          {isLoading ? (
            // Skeleton: 5 placeholder boxes
            Array.from({ length: 5 }).map((_, i) => (
              <View key={`crew-skel-${i}`} style={crewRowStyles.thumbSkeleton} />
            ))
          ) : (
            <>
              {items.slice(0, 5).map((it) => (
                <View key={`crew-thumb-${it.item_id}`} style={crewRowStyles.thumbWrap}>
                  {it.primary_photo_url ? (
                    <Image
                      source={{ uri: it.primary_photo_url }}
                      style={crewRowStyles.thumb}
                      accessibilityIgnoresInvertColors
                    />
                  ) : (
                    <View style={[crewRowStyles.thumb, crewRowStyles.thumbPlaceholder]}>
                      <MaterialIcons
                        name="image"
                        size={16}
                        color={fpColors.outline}
                      />
                    </View>
                  )}
                </View>
              ))}
              {total > 5 && (
                <View style={crewRowStyles.overflow}>
                  <Text style={crewRowStyles.overflowText}>
                    +{total - 5}
                  </Text>
                </View>
              )}
            </>
          )}
        </View>
      )}

      <View style={sectionedStyles.crewCTA}>
        <Text style={sectionedStyles.crewCTAText}>Open circle</Text>
        <MaterialIcons name="chevron-right" size={16} color={fpColors.primary} />
      </View>
    </Pressable>
  );
};

const crewRowStyles = StyleSheet.create({
  thumbStrip: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 8,
    marginLeft: 28,
  },
  thumbWrap: {
    width: 52,
    height: 52,
  },
  thumb: {
    width: 52,
    height: 52,
    borderRadius: 6,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  thumbPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbSkeleton: {
    width: 52,
    height: 52,
    borderRadius: 6,
    backgroundColor: fpColors.surfaceContainerLow,
    opacity: 0.6,
  },
  overflow: {
    width: 52,
    height: 52,
    borderRadius: 6,
    backgroundColor: fpColors.surfaceContainerLow,
    alignItems: 'center',
    justifyContent: 'center',
  },
  overflowText: {
    fontSize: 12,
    fontWeight: '600',
    color: fpColors.primary,
  },
});
